// src/context/ReminderContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';

const ReminderContext = createContext(null);

// ── Gentle Synthetic Web Audio Chime (Apple Pro Sound) ────────
function playReminderChime() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const now = ctx.currentTime;

    // First Harmonic Tone (D5 - 587 Hz)
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(587.33, now);
    gain1.gain.setValueAtTime(0, now);
    gain1.gain.linearRampToValueAtTime(0.12, now + 0.04);
    gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.35);
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.start(now);
    osc1.stop(now + 0.4);

    // Second Harmonic Tone (A5 - 880 Hz)
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(880, now + 0.1);
    gain2.gain.setValueAtTime(0, now + 0.1);
    gain2.gain.linearRampToValueAtTime(0.16, now + 0.15);
    gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.65);
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.start(now + 0.1);
    osc2.stop(now + 0.7);
  } catch (_) {}
}

export function ReminderProvider({ children }) {
  const { user } = useAuth();
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading]     = useState(false);
  const notifiedIdsRef           = useRef(new Set());

  // ── Fetch user reminders ───────────────────────────────────
  const fetchReminders = useCallback(async () => {
    if (!user || window.location.pathname === "/onboarding" || window.location.pathname === "/") return;
    try {
      setLoading(true);
      const res = await api.get('/reminders/my');
      const list = Array.isArray(res.data) ? res.data : [];
      setReminders(list);
    } catch (_) {
      // Silently ignore if offline
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchReminders();
  }, [fetchReminders]);

  // ── Categorized Views ──────────────────────────────────────
  const activeReminders = useMemo(() => {
    return reminders.filter(r => !r.isCompleted);
  }, [reminders]);

  const completedReminders = useMemo(() => {
    return reminders.filter(r => r.isCompleted);
  }, [reminders]);

  const dueReminders = useMemo(() => {
    const now = new Date();
    return activeReminders.filter(r => new Date(r.remindAt) <= now);
  }, [activeReminders]);

  const dueCount = dueReminders.length;

  // ── Operations ─────────────────────────────────────────────
  const createReminder = async ({ leadId, title, remindAt }) => {
    try {
      const res = await api.post('/reminders', { leadId, title, remindAt });
      toast.success('Reminder scheduled');
      fetchReminders();
      return res.data;
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to create reminder');
      throw err;
    }
  };

  const completeReminder = async (id, isCompleted = true) => {
    try {
      await api.put(`/reminders/${id}/complete`, { isCompleted });
      setReminders(prev =>
        prev.map(r => r.id === id ? { ...r, isCompleted: isCompleted ? 1 : 0 } : r)
      );
      toast.success(isCompleted ? 'Reminder completed' : 'Reminder reopened');
    } catch (err) {
      toast.error('Failed to update reminder');
    }
  };

  const snoozeReminder = useCallback(async (id, minutes = 15) => {
    try {
      const res = await api.put(`/reminders/${id}/snooze`, { minutes });
      toast.success(`Snoozed for ${minutes >= 60 ? `${minutes / 60} hour(s)` : `${minutes} mins`}`);
      notifiedIdsRef.current.delete(id);
      fetchReminders();
      return res.data;
    } catch (err) {
      toast.error('Failed to snooze reminder');
    }
  }, [fetchReminders]);

  const deleteReminder = async (id) => {
    try {
      await api.delete(`/reminders/${id}`);
      setReminders(prev => prev.filter(r => r.id !== id));
      toast.success('Reminder removed');
    } catch (err) {
      toast.error('Failed to delete reminder');
    }
  };

  // ── Periodic Due Reminder Checker (every 25 seconds) ────────
  useEffect(() => {
    if (!user) return;

    const checkDueReminders = () => {
      const now = new Date();
      activeReminders.forEach(r => {
        const dueDate = new Date(r.remindAt);
        if (dueDate <= now && !r.isNotified && !notifiedIdsRef.current.has(r.id)) {
          // Register alert to prevent repeated spam
          notifiedIdsRef.current.add(r.id);
          api.put(`/reminders/${r.id}/notified`).catch(() => {});

          // Trigger soft audio chime
          playReminderChime();

          // Native browser notification (if permitted)
          if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
            try {
              new Notification(`Lead Reminder: ${r.leadName || 'Lead'}`, {
                body: r.title,
                icon: '/logo192.png'
              });
            } catch (_) {}
          }

          // In-App Toast Alert with interactive actions
          toast((t) => (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 240 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--text)' }}>
                  Lead Reminder Due
                </span>
                <span style={{ fontSize: 11, color: 'var(--warning)', fontWeight: 600 }}>Due Now</span>
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--text-2)' }}>
                <strong>{r.leadName || 'Lead'}</strong>: {r.title}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <button
                  type="button"
                  style={{
                    background: 'var(--primary)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 6,
                    padding: '4px 10px',
                    fontSize: 11.5,
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    toast.dismiss(t.id);
                    window.location.href = `/dashboard/leads/${r.leadId}`;
                  }}
                >
                  View Lead
                </button>
                <button
                  type="button"
                  style={{
                    background: 'var(--surface-2)',
                    color: 'var(--text)',
                    border: '1px solid var(--border)',
                    borderRadius: 6,
                    padding: '4px 8px',
                    fontSize: 11.5,
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    toast.dismiss(t.id);
                    snoozeReminder(r.id, 15);
                  }}
                >
                  Snooze 15m
                </button>
              </div>
            </div>
          ), {
            duration: 9000,
            style: {
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: '12px',
              boxShadow: 'var(--shadow-lg)'
            }
          });
        }
      });
    };

    checkDueReminders();
    const interval = setInterval(checkDueReminders, 25000);
    return () => clearInterval(interval);
  }, [user, activeReminders, snoozeReminder]);

  // Request browser notification permissions on demand
  const requestDesktopPermission = useCallback(async () => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      if (Notification.permission === 'default') {
        const result = await Notification.requestPermission();
        if (result === 'granted') {
          toast.success('Desktop notifications enabled');
        }
      }
    }
  }, []);

  const value = {
    reminders,
    activeReminders,
    completedReminders,
    dueReminders,
    dueCount,
    loading,
    refreshReminders: fetchReminders,
    createReminder,
    completeReminder,
    snoozeReminder,
    deleteReminder,
    requestDesktopPermission,
  };

  return (
    <ReminderContext.Provider value={value}>
      {children}
    </ReminderContext.Provider>
  );
}

export function useReminders() {
  const context = useContext(ReminderContext);
  if (!context) {
    throw new Error('useReminders must be used within a ReminderProvider');
  }
  return context;
}
